import { isEmpty } from 'ramda';
import {
  Asset,
  ProductWithQualityInfo,
  ProductWithSides,
  QualityCheck,
  Side,
  SideWithAsset,
} from 'shared-types';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import API from 'services/API';

import { getTotalProductsCount, updateProductQualityInfo } from 'utils/misc';

import useAssetsStore from './useAssetsStore';
import useLayoutStore from './useLayoutStore';
import useSessionStore from './useSessionStore';

type WithAsset = SideWithAsset;
type Product = ProductWithSides | ProductWithSides<WithAsset> | ProductWithQualityInfo;

type ProductsState = {
  products: Product[] | [];
  totalProductsCount: number;
  selectedSideId?: string;
  selectedProductId: string | null;
};
type UpdatePageNumberParams = {
  assetId: Asset['id'];
  productId: ProductWithSides['id'];
  pageNumber: Asset['pageNumber'];
};

type ProductsActions = {
  setProducts: (products: Product[]) => void;
  updateAssetPageNumber: (params: UpdatePageNumberParams) => void;
  removeAssetFromSide: (productId: ProductWithSides['id'], sideId: Side['id']) => void;
  removeSideFromProduct: (productId: ProductWithSides['id'], sideId: Side['id']) => void;
  setSelectedSideId: (sideId: Side['id']) => void;
  setSelectedProductId: (productId: string | null) => void;
  changeProductCount: (
    productId: ProductWithSides['id'],
    newCount: ProductWithSides['count'],
  ) => void;
  selectAssetForSide: (params: {
    assetId: Asset['id'];
    sideId: Side['id'];
    productId: string;
  }) => void;
  updateSide: (params: {
    productId: ProductWithSides['id'];
    sideId: Side['id'];
    actions: Side['actions'];
  }) => void;
  updateSidePreview: (params: {
    productId: ProductWithSides['id'];
    sideId: Side['id'];
    previewImageUrl: Side['previewImageUrl'];
    previewThumbnailUrl: Side['previewThumbnailUrl'];
    resizeMetadata: Side['resizeMetadata'];
    qualityCheck?: QualityCheck;
  }) => void;
  deleteProduct: (productId: ProductWithSides['id']) => void;
  getSide: (sideId: Side['id']) => Side | SideWithAsset | undefined;

  // Computed selectors
  getSelectedProduct: () => Product | null;
  getSelectedSide: () => Side | SideWithAsset | undefined;
  getSelectedProductSides: () => Side[];
};

const useProductsStore = create(
  subscribeWithSelector(
    immer<ProductsState & ProductsActions>((set, get) => ({
      products: [],
      totalProductsCount: 0,
      selectedProductId: null,
      setProducts: (products: ProductWithSides[]) => {
        set((state) => {
          state.products = products;
        });
      },
      updateAssetPageNumber: ({ pageNumber, productId, assetId }) => {
        set((state) => {
          state.products = state.products.map((p) =>
            p.id === productId
              ? {
                  ...p,
                  sides: p.sides.map((s) => {
                    if (s.assetId === assetId) {
                      // Maak een kopie van 's' met een bijgewerkte pageNumber voor het asset
                      const sideWithAsset = s as SideWithAsset;
                      if (sideWithAsset.asset) {
                        return {
                          ...sideWithAsset,
                          asset: {
                            ...sideWithAsset.asset,
                            pageNumber,
                          },
                        };
                      }
                    }
                    return s;
                  }),
                }
              : p,
          );
        });
      },
      setSelectedSideId: (sideId: Side['id']) => {
        set((state) => {
          state.selectedSideId = sideId;
        });
      },
      setSelectedProductId: (productId: string | null) => {
        set((state) => {
          state.selectedProductId = productId;
        });
      },
      changeProductCount: (
        productId: ProductWithSides['id'],
        newCount: ProductWithSides['count'],
      ) => {
        set((state) => {
          state.products = state.products.map((p) =>
            p.id === productId ? { ...p, count: newCount } : p,
          );
        });
      },
      removeAssetFromSide: (productId: ProductWithSides['id'], sideId: Side['id']) => {
        set((state) => {
          state.products = state.products.map((p) =>
            p.id === productId
              ? {
                  ...p,
                  sides: p.sides.map((s) => (s.id === sideId ? { ...s, assetId: null } : s)),
                }
              : p,
          );
        });
      },
      removeSideFromProduct: (productId: ProductWithSides['id'], sideId: Side['id']) => {
        set((state) => {
          state.products = state.products.map((p) =>
            p.id === productId ? { ...p, sides: p.sides.filter((s) => s.id !== sideId) } : p,
          );
        });
      },
      selectAssetForSide: ({ assetId, productId, sideId }) => {
        const asset = useAssetsStore.getState().getAsset(assetId);

        if (!asset) {
          return;
        }

        set((state) => {
          const productIndex = state.products.findIndex((p) => p.id === productId);
          if (productIndex === -1) {
            return;
          }

          const sideIndex = state.products[productIndex].sides.findIndex((s) => s.id === sideId);
          if (sideIndex === -1) {
            return;
          }

          // Maak een cast naar SideWithAsset en zet zowel assetId als asset
          const sideWithAsset = state.products[productIndex].sides[
            sideIndex
          ] as unknown as SideWithAsset;
          sideWithAsset.assetId = assetId;
          sideWithAsset.asset = asset;
        });
      },
      deleteProduct: (productId) => {
        set((state) => {
          state.products = state.products.filter((p) => p.id !== productId);
        });
      },
      updateSide: ({ productId, sideId, actions }) => {
        set((state) => {
          state.products = state.products.map((p) =>
            p.id === productId
              ? {
                  ...p,
                  sides: p.sides.map((s) => (s.id === sideId ? { ...s, actions } : s)),
                }
              : p,
          );
        });
      },
      updateSidePreview: ({
        productId,
        sideId,
        previewImageUrl,
        previewThumbnailUrl,
        resizeMetadata,
        qualityCheck,
      }) => {
        set((state) => {
          state.products = state.products.map((p) => {
            if (p.id === productId) {
              const updatedProduct = {
                ...p,
                sides: p.sides.map((s: any) => {
                  if (s.id === sideId) {
                    const updatedSide = { ...s, previewImageUrl, previewThumbnailUrl, resizeMetadata };
                    // Add qualityCheck if it's a SideWithAsset and qualityCheck is provided
                    if (qualityCheck && s.asset) {
                      updatedSide.qualityCheck = qualityCheck;
                    }
                    return updatedSide;
                  }
                  return s;
                }),
              };

              // If this is a ProductWithQualityInfo, recalculate quality info including sidesWithWarnings
              if ('sidesWithWarnings' in updatedProduct && qualityCheck) {
                const session = useSessionStore.getState().session;
                if (session) {
                  const totalSidesCount = updatedProduct.sides.length;
                  return updateProductQualityInfo(
                    updatedProduct as ProductWithQualityInfo,
                    session,
                    totalSidesCount,
                  );
                }
              }

              return updatedProduct;
            }
            return p;
          });
        });
      },

      getSide: (sideId: Side['id']) => {
        if (!sideId) {
          return undefined;
        }

        // Doorzoek alle producten voor de gegeven side ID
        for (const product of get().products) {
          for (const side of product.sides) {
            if (side.id === sideId) {
              return side;
            }
          }
        }

        return undefined;
      },

      // Computed selectors
      getSelectedProduct: () => {
        const { selectedProductId, products } = get();
        if (!selectedProductId) return null;
        return products.find((p) => p.id === selectedProductId) || null;
      },

      getSelectedSide: () => {
        const { selectedSideId } = get();
        if (!selectedSideId) return undefined;
        return get().getSide(selectedSideId);
      },

      getSelectedProductSides: () => {
        const selectedProduct = get().getSelectedProduct();
        return selectedProduct?.sides || [];
      },
    })),
  ),
);

// totalProductsCount changes to update shouldDisplayAddProduct
useProductsStore.subscribe(
  (state) => state.products,
  (products) => {
    const session = useSessionStore.getState().session;
    if (!session || isEmpty(session)) {
      return;
    }

    const { quantity, confirmation } = session;
    const totalProductsCount = getTotalProductsCount(products);
    useProductsStore.setState((state) => {
      state.totalProductsCount = totalProductsCount;
    });

    const shouldDisplayAddProduct = totalProductsCount < quantity;
    // Fix voor de type error: gebruik een object in plaats van een callback
    useLayoutStore.setState({ shouldDisplayAddProduct });

    // if session is confirmed, then products changed then setShouldHideConfirmation to false
    // to show the confirmation again
    if (confirmation.confirmed) {
      useLayoutStore.getState().setShouldHideConfirmation(false);
      API.confirmSession(session.id, { confirmed: false }).catch((err) => {
        console.error('Error updating session confirmation:', err);
      });
    }
  },
);

// Auto-select first product if none is selected, or if selected product doesn't exist
useProductsStore.subscribe(
  (state) => ({ products: state.products, selectedProductId: state.selectedProductId }),
  ({ products, selectedProductId }) => {
    if (products.length > 0) {
      if (!selectedProductId || !products.find((p) => p.id === selectedProductId)) {
        useProductsStore.getState().setSelectedProductId(products[0].id);
      }
    }
  },
);

// Auto-reset selectedSideId when switching products if current side doesn't belong to selected product
useProductsStore.subscribe(
  (state) => ({ selectedProductId: state.selectedProductId, selectedSideId: state.selectedSideId }),
  ({ selectedProductId, selectedSideId }) => {
    if (selectedProductId && selectedSideId) {
      const selectedProduct = useProductsStore.getState().getSelectedProduct();
      const sides = selectedProduct?.sides || [];

      // If current selectedSideId doesn't belong to the current product, reset it
      if (!sides.find((side) => side.id === selectedSideId)) {
        useProductsStore.getState().setSelectedSideId(sides[0]?.id || '');
      }
    }
  },
);

// Helper functie voor debugging in de console
if (typeof window !== 'undefined') {
  (window as any).debugStores = {
    ...((window as any).debugStores || {}),
    products: {
      getState: () => useProductsStore.getState(),
      subscribe: (callback: (state: ProductsState & ProductsActions) => void) =>
        useProductsStore.subscribe(callback),
    },
  };
}

export default useProductsStore;
